/**
 * The night's fixtures: what each group still has to play, and the bracket
 * once there is one.
 *
 * ONE TAB, NOT TWO. V1 kept a separate knockout tab, which meant that at the
 * moment the groups ended the thing an admin wanted moved to a tab they were
 * not on. The stage decides what this shows; nobody has to know that it did.
 *
 * The group half answers "who is on next", which a standings table cannot —
 * it aggregates away the fixtures it was built from. Per-group standings live
 * on the public /venues/:slug/standings page.
 *
 * Match rows come from GET .../matches ALREADY IN PLAY ORDER, so nothing here
 * sorts. The order is the sign-flipped rule shared with the Durable Object and
 * /boards; re-deriving it in a fourth place is how a list ends up disagreeing
 * with the board it describes.
 *
 * THREE STATES, not two. Groups running; groups done but the wildcards not yet
 * picked; and the bracket. The middle one only exists on a night where the
 * bracket size does not divide evenly across the groups — on a clean split the
 * room generates unattended and the tab goes straight from the first to the
 * third, usually within a second or two of the last leg.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api.js';

/**
 * The endpoint already names each round, so this is only the fallback for a
 * round it did not name. Naming rounds in two places is how the bracket and
 * the board cards end up calling the same match different things.
 */
const roundLabel = (round) => (round === 1 ? 'Final' : `Last ${2 ** round}`);

/**
 * Two maps, deliberately separate: what a human reads, and which badge
 * treatment it borrows. The variant names describe the look, not the status —
 * a live match takes the gold badge the same way a live tournament does.
 */
const STATUS_LABELS = {
  pending:   'Pending',
  live:      'In progress',
  completed: 'Completed',
  forfeit:   'Forfeit',
};

const STATUS_VARIANTS = {
  pending:   'neutral',
  live:      'gold',
  completed: 'success',
  forfeit:   'danger',
};

/**
 * Buckets the flat match list by group, keeping the order it arrived in.
 *
 * Knockout rows carry a null groupId and are dropped here — the bracket below
 * is where they belong, and listing them twice would make the tab read as
 * though the night had more matches than it does.
 *
 * A group's board comes off its first match rather than being fetched: every
 * match in a group is drawn onto the same board, and /matches carries it.
 */
function bucketByGroup(matches) {
  const groups = new Map();

  for (const match of matches ?? []) {
    if (match.groupId == null) continue;

    if (!groups.has(match.groupId)) {
      groups.set(match.groupId, {
        groupId:     match.groupId,
        groupNumber: match.groupNumber,
        boardNumber: match.boardNumber,
        matches:     [],
      });
    }

    groups.get(match.groupId).matches.push(match);
  }

  return [...groups.values()]
    .sort((a, b) => (a.groupNumber ?? 0) - (b.groupNumber ?? 0));
}

/* ------------------------------------------------------------------------- */

function PlayerName({ player }) {
  return player
    ? <>{player.displayName}</>
    : <span className="hd-muted">—</span>;
}

/**
 * One fixture.
 *
 * `showStatus` is off in the Upcoming column, where every row is pending by
 * construction and a column of identical badges is noise.
 *
 * legScore is absent on anything not played out — a forfeit's leg tally is
 * whatever had been won when the entrant left, and a bye's is 0–0. The status
 * badge already says which, so there is nothing to put in its place.
 */
function MatchRow({ match, showStatus }) {
  const marker = match.marker?.displayName ?? null;

  return (
    <li className="hd-fixture-match">
      <div className="hd-fixture-line">
        <span className="hd-fixture-players">
          <PlayerName player={match.player1} />
          <span className="hd-muted"> v </span>
          <PlayerName player={match.player2} />
        </span>

        {match.legScore && (
          <span className="hd-fixture-score">{match.legScore}</span>
        )}
      </div>

      <div className="hd-fixture-line hd-fixture-sub">
        <span className="hd-meta">
          {marker
            ? <>Marker: {marker}</>
            : <span className="hd-muted">Marker not yet assigned</span>}
        </span>

        {showStatus && (
          <span className={`hd-badge hd-badge-${STATUS_VARIANTS[match.status] ?? 'neutral'}`}>
            {STATUS_LABELS[match.status] ?? match.status}
          </span>
        )}
      </div>
    </li>
  );
}

function MatchList({ matches, showStatus, emptyNote }) {
  if (!matches.length) {
    return <p className="hd-fixture-none hd-muted">{emptyNote}</p>;
  }

  return (
    <ul className="hd-fixture-list">
      {matches.map((match) => (
        <MatchRow key={match.id} match={match} showStatus={showStatus} />
      ))}
    </ul>
  );
}

function GroupColumn({ group }) {
  // 'pending' is the whole definition of upcoming: a live match is being
  // played, not waiting to be. No sort — the list arrived in play order.
  const upcoming = group.matches.filter((match) => match.status === 'pending');

  return (
    <section className="hd-card hd-fixture-group">
      <h3 className="hd-section-title">
        Group {group.groupNumber}
        {group.boardNumber != null && (
          <span className="hd-meta"> · board {group.boardNumber}</span>
        )}
      </h3>

      <div className="hd-fixture-section">
        <div className="hd-fixture-section-label">Upcoming</div>
        <MatchList
          matches={upcoming}
          showStatus={false}
          emptyNote="Nothing left to play."
        />
      </div>

      <div className="hd-fixture-section">
        <div className="hd-fixture-section-label">All matches</div>
        <MatchList
          matches={group.matches}
          showStatus
          emptyNote="No matches drawn."
        />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * A name alone does not identify anybody.
 *
 * Two entries can carry the SAME display name — a guest linked to a
 * placeholder account resolves to the same string as the real player, and the
 * staging dataset has exactly that: two entries both reading "Dave Double", in
 * different groups, with different records. An admin picking a wildcard off a
 * list of bare names would be choosing blind between them.
 *
 * Group and finishing place disambiguate, and are worth showing regardless:
 * "third in group 2" is how the wildcard conversation is actually had.
 */
function PlayerTag({ player }) {
  return (
    <>
      {player.displayName}
      <span className="hd-meta"> · G{player.groupNumber} #{player.place}</span>
    </>
  );
}

/** The same identity, flattened — a <select> cannot hold markup. */
const playerOptionLabel = (p) =>
  `${p.displayName} · G${p.groupNumber} #${p.place}`
  + ` · W${p.matchesWon}/${p.matchesPlayed}, ${p.legDifference >= 0 ? '+' : ''}${p.legDifference} legs`;

/** Tier 0 is the group winners, 1 the runners-up, and so on down. */
const tierLabel = (tier) => {
  if (tier === 0) return 'Group winners';
  if (tier === 1) return 'Runners-up';
  return `${tier + 1}th place`;
};

/**
 * Groups done, no bracket, wildcards to settle.
 *
 * Everything shown comes from GET knockout/preview, which computes it with the
 * same qualificationSplit and buildSeedList that generation uses — so
 * confirming produces exactly what was on screen. That is the whole point of
 * the panel; a preview derived some other way would be a preview of nothing.
 */
function KnockoutConfirmPanel({ venueId, tournamentId, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [picks, setPicks]     = useState(null);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(false);

  const base = `/api/venues/${venueId}/tournaments/${tournamentId}`;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data, error: err, status } = await apiGet(`${base}/knockout/preview`);
      if (cancelled) return;

      // 409 means there is nothing left to decide — the group stage moved on
      // between this panel mounting and the request landing, which on a clean
      // split takes about a second. The refresh is already on its way from the
      // knockout_generated broadcast, so saying nothing is right; an error
      // about a bracket that now exists would be noise.
      if (status === 409) return;

      if (err) { setError(err); return; }

      setPreview(data);
      setPicks(data.wildcards.map((w) => w.entryId));
    })();

    return () => { cancelled = true; };
  }, [base]);

  /**
   * Everything except whoever holds ANOTHER slot, so two slots cannot be given
   * the same player. Recommended first; the rest after.
   *
   * A pick the room chose automatically appears in the second group rather than
   * the first, because `recommended` is defined as the near-misses the
   * comparator did NOT take.
   */
  const optionsFor = useCallback((slotIndex) => {
    const taken  = new Set(picks.filter((_, j) => j !== slotIndex));
    const free   = preview.fallback.filter((p) => !taken.has(p.entryId));
    const recIds = new Set(preview.recommended.map((p) => p.entryId));

    return {
      recommended: free.filter((p) => recIds.has(p.entryId)),
      rest:        free.filter((p) => !recIds.has(p.entryId)),
    };
  }, [picks, preview]);

  const confirm = async () => {
    setBusy(true);
    setError(null);

    const { error: err, status } = await apiPost(`${base}/knockout/generate`, {
      wildcardEntryIds: picks,
    });

    setBusy(false);

    // Somebody else — or the room itself — generated between this panel
    // loading and this click. Nothing is wrong and there is nothing left to
    // decide, so refresh into the bracket rather than reporting a conflict.
    if (status === 409) { onChanged(); return; }
    if (err) { setError(err); return; }

    onChanged();
  };

  if (error && !preview) {
    return (
      <section className="hd-card hd-confirm-panel">
        <h2 className="hd-section-title">Knockout</h2>
        <p className="hd-muted">{error}</p>
      </section>
    );
  }

  if (!preview || !picks) {
    return <div className="hd-empty">Working out who has qualified…</div>;
  }

  const { qualifiersPerGroup, groupCount, wildcardSlots, bracketSize, qualifiers } = preview;

  // Grouped by finishing position, which is how they were seeded — a flat list
  // would lose the reason a given player is in it.
  const tiers = [...new Set(qualifiers.map((q) => q.tier))].sort((a, b) => a - b);

  return (
    <section className="hd-card hd-confirm-panel">
      <h2 className="hd-section-title">Confirm the knockout</h2>

      <p className="hd-meta">
        {bracketSize} qualify — {qualifiersPerGroup} from each of {groupCount} group
        {groupCount === 1 ? '' : 's'}
        {wildcardSlots > 0 && <>, plus {wildcardSlots} wildcard{wildcardSlots === 1 ? '' : 's'}</>}.
      </p>

      {tiers.map((tier) => (
        <div className="hd-confirm-tier" key={tier}>
          <div className="hd-fixture-section-label">{tierLabel(tier)}</div>
          <ul className="hd-confirm-list">
            {qualifiers.filter((q) => q.tier === tier).map((q) => (
              <li key={q.entryId}>
                <PlayerTag player={q} />
                <span className="hd-meta">
                  {' '}· W{q.matchesWon}/{q.matchesPlayed}
                  , {q.legDifference >= 0 ? '+' : ''}{q.legDifference} legs
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {wildcardSlots > 0 && (
        <div className="hd-confirm-tier">
          <div className="hd-fixture-section-label">
            Wildcards — the best records among those who did not qualify on placing
          </div>

          {picks.map((entryId, i) => {
            const { recommended, rest } = optionsFor(i);

            return (
              <div className="hd-confirm-slot" key={i}>
                <label className="hd-meta" htmlFor={`wildcard-${i}`}>
                  Wildcard {i + 1}
                </label>
                <select
                  id={`wildcard-${i}`}
                  className="hd-select"
                  value={entryId}
                  disabled={busy}
                  onChange={(e) => {
                    const next = [...picks];
                    next[i] = Number(e.target.value);
                    setPicks(next);
                  }}
                >
                  {recommended.length > 0 && (
                    <optgroup label="Recommended">
                      {recommended.map((p) => (
                        <option key={p.entryId} value={p.entryId}>{playerOptionLabel(p)}</option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="Any group-stage player">
                    {rest.map((p) => (
                      <option key={p.entryId} value={p.entryId}>{playerOptionLabel(p)}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
            );
          })}
        </div>
      )}

      {error && <p className="hd-muted">{error}</p>}

      <div className="hd-confirm-actions">
        <button
          type="button"
          className="hd-btn hd-btn-primary"
          disabled={busy}
          onClick={confirm}
        >
          {busy ? 'Generating…' : 'Generate the bracket'}
        </button>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------- */

function BracketView({ bracket }) {
  return (
    // Horizontal scroll is the bracket's own, not the page's — a deep bracket
    // must never make the whole admin screen scroll sideways.
    <div className="hd-bracket-wrap">
      <div className="hd-bracket">
        {bracket.rounds.map((round) => (
          <div className="hd-bracket-round" key={round.round}>
            <h3 className="hd-bracket-round-title">
              {round.roundName ?? roundLabel(round.round)}
            </h3>

            {round.matches.map((match) => (
              <div className="hd-bracket-match" key={match.id}>
                {[match.player1, match.player2].map((player, i) => {
                  const won = match.winner && player && match.winner.entryId === player.entryId;
                  return (
                    <div
                      className={`hd-bracket-slot ${won ? 'hd-bracket-won' : ''}`}
                      key={player?.entryId ?? `slot-${match.id}-${i}`}
                    >
                      {player?.displayName ?? <span className="hd-muted">—</span>}
                    </div>
                  );
                })}

                {match.isBye && <div className="hd-bracket-note">Bye</div>}
                {match.status === 'forfeit' && <div className="hd-bracket-note">Forfeit</div>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */

export default function FixturesTab({ venueId, tournamentId, onChanged, matches, bracket }) {
  // A bracket exists the moment the knockout is generated, and from then on it
  // is the live picture. The groups stay reachable below it because who
  // qualified on what record is still the question people ask.
  const hasBracket = (bracket?.rounds?.length ?? 0) > 0;
  const groups     = bucketByGroup(matches);

  // Two booleans rather than a three-valued state variable: the group half of
  // this tab renders in all three cases, and only the top half changes.
  const groupMatches   = (matches ?? []).filter((m) => m.groupId != null);
  const groupsComplete = groupMatches.length > 0
    && !groupMatches.some((m) => m.status === 'pending' || m.status === 'live');

  // Groups done, no bracket. On a clean split the room generates unattended and
  // this state lasts a second or two; on a wildcard night it is where the tab
  // waits for an admin to settle the picks.
  const needsConfirmation = groupsComplete && !hasBracket;

  if (!hasBracket && groups.length === 0) {
    return <div className="hd-empty">No fixtures yet — nothing has been drawn.</div>;
  }

  return (
    <div className="hd-stack">
      {hasBracket && <BracketView bracket={bracket} />}

      {needsConfirmation && (
        <KnockoutConfirmPanel
          venueId={venueId}
          tournamentId={tournamentId}
          onChanged={onChanged}
        />
      )}

      {groups.length > 0 && (
        <>
          {(hasBracket || needsConfirmation) && (
            <h2 className="hd-section-title">Group stage</h2>
          )}

          <div className="hd-fixture-grid">
            {groups.map((group) => (
              <GroupColumn key={group.groupId} group={group} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
